import { DropdownButton } from '@/components/elements/forms/DropdownButton';
import Icon from '@/components/icons/Icon';
import { Dropdown } from '@/components/modules/dropdown';
import { FINNISH_TRANSLATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import { footerTosItems } from '@/constants/footerNavigationConstants';
import { isServer, isSistaminuten, url } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { _s, getLang, getLanguages, setLang } from '@/locale';
import { Lang } from '@/locale/i18n';
import { getScreenName } from '@/services/navigationServices';
import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';

const SocialMediaItems = () => {
  return (
    <Fragment>
      <a key="instagram" target="_blank" rel="noreferrer" href="https://instagram.com/bokadirekt_se">
        <Icon variant="instagram" className="h-6" alt="instagram" />
      </a>
      <a
        key="linkedin"
        target="_blank"
        rel="noreferrer"
        href="https://www.linkedin.com/company/bokadirekt-i-stockholm-ab/">
        <Icon variant="linkedin" className="h-6" alt="linkedin" />
      </a>
      <a key="facebook" target="_blank" rel="noreferrer" href="https://www.facebook.com/bokadirekt">
        <Icon variant="facebook" className="h-6" alt="facebook" />
      </a>
    </Fragment>
  );
};

export const PaymentLogos = () => {
  return (
    <Fragment>
      <Icon key="klarna" noFilter variant="payment-method-klarna" className="h-3xl" />
      <Icon key="qliro" variant="payment-method-qliro" className="h-3xl" noFilter />
      <Icon key="visa" noFilter variant="payment-method-visa" className="h-3xl" />
      <Icon key="mastercard" noFilter variant="payment-method-mastercard" className="h-3xl" />
      <Icon key="apple-pay" noFilter variant="payment-method-apple-pay" className="h-3xl" />
      <Icon key="google-pay" noFilter variant="payment-method-google-pay" className="h-3xl" />
    </Fragment>
  );
};

const getTosItems = (className: string) => {
  return (
    <div className={`flex-wrap gap-4 ${className}`}>
      {footerTosItems.map((link, key) => {
        if (link.link) {
          return (
            <Link key={key} className="text-black-900 hover:text-black-900 text-m" to={link.url}>
              {link.title}
            </Link>
          );
        } else {
          return (
            <a key={key} href={link.url} className="text-black-900 hover:text-black-900 text-m">
              {link.title}
            </a>
          );
        }
      })}
    </div>
  );
};

const LanguageItem = ({ isListPage, className }: { isListPage: boolean; className: string }) => {
  const [showLang, setShowLang] = useState(false);
  const getVariant = useGetAmplitudeExperimentVariant();
  const flags = useAppSelector((state) => state.flags);
  const finnishTranslationFeature =
    flags[FINNISH_TRANSLATION_FEATURE_FLAG]?.value === 'on' ||
    getVariant(FINNISH_TRANSLATION_FEATURE_FLAG)?.value === 'on';

  const changeLanguage = (lang: Lang) => () => {
    setLang(lang);
  };

  const languages = getLanguages().filter((lang) => lang !== 'fi' || finnishTranslationFeature);

  const languageItems = (
    <ul className="navbar-nav">
      {languages.map((lang, index) => {
        const isActive = lang === getLang();
        const getFlagIcon = () => {
          switch (lang) {
            case 'sv':
              return <Icon variant="swedish" noFilter className="mr-2" />;
            case 'en':
              return <Icon variant="english" noFilter className="mr-2" />;
            case 'fi':
              return <Icon variant="finnish" noFilter className="mr-2" />;
            default:
              return null;
          }
        };

        return (
          <li
            key={index}
            className={`flex cursor-pointer items-center px-2 py-3 ${isActive ? 'bg-black-100' : ''}`}
            onClick={changeLanguage(lang)}>
            {getFlagIcon()}
            {<span>{_s(lang)}</span>}
          </li>
        );
      })}
    </ul>
  );

  const toggleShowLang = () => {
    const screenName = isListPage ? 'search_results' : getScreenName(url.getScreenNameFromUrl(window.location.href));
    setShowLang((prev) => !prev);
  };

  return (
    <div key="language" className={`relative ${className}`}>
      <DropdownButton
        icon={<Icon variant="globe" />}
        close={() => setShowLang(false)}
        isActive={false}
        toggleOpen={toggleShowLang}
        className="!bg-brown-50">
        {_s(getLang() + 'Short')}
      </DropdownButton>
      {showLang && (
        <Dropdown
          allowClick={showLang}
          className="bottom-[32px] left-auto right-0 top-auto w-[200px] overflow-hidden"
          onClickOutside={() => setShowLang(false)}
          children={languageItems}
        />
      )}
    </div>
  );
};

export const FooterBottom = ({ isListPage }: { isListPage: boolean }) => {
  return !isSistaminuten() ? (
    <>
      {getTosItems('flex md:hidden ')}
      <div className="flex items-center justify-center gap-3 md:hidden">
        <SocialMediaItems />
      </div>
      <div className="hidden flex-wrap items-center justify-between md:flex">
        <SocialMediaItems />
        <PaymentLogos />
        {!isServer && <LanguageItem isListPage={isListPage} className="hidden md:block" />}
      </div>
      <div className="flex flex-1 items-center justify-between gap-2">
        <div className="text-m flex-shrink-0">&copy; Bokadirekt {new Date().getFullYear()}</div>
        {!isServer && <LanguageItem isListPage={isListPage} className="block md:hidden" />}
        {getTosItems('hidden md:flex')}
      </div>
    </>
  ) : (
    <>
      <div className="flex flex-1 items-center justify-between">
        <div className="text-m">&copy; Bokadirekt {new Date().getFullYear()}</div>
        {!isServer && <LanguageItem isListPage={isListPage} className="" />}
      </div>
    </>
  );
};
